import { datadogLogs } from "@datadog/browser-logs";

const IS_MONITORING_ENABLED =
  process.env.NEXT_PUBLIC_IS_MONITORING_ENABLED === "true";
const IS_DATADOG_ENABLED =
  process.env.NEXT_PUBLIC_IS_DATADOG_ENABLED === "true";

/**
 * 緊急度: レベル1
 *
 * システム全体の停止や即時対応が必要な状況を記録します。
 *
 * @param {string} message - 緊急メッセージ。
 * @param {object} [messageContext] - メッセージに関連する追加のコンテキスト情報。
 * @param {Error} [error] - エラーオブジェクト。
 */
export const emerg = (
  message: string,
  messageContext?: object,
  error?: Error,
) => {
  if (!IS_MONITORING_ENABLED) return;
  if (!IS_DATADOG_ENABLED) {
    console.error(message, messageContext, error);
    return;
  }
  datadogLogs.logger.emerg(message, messageContext, error);
};

/**
 * 緊急度: レベル2
 *
 * 即時対応が必要な重大な問題を記録するために使用します。
 *
 * @param {string} message - アラートメッセージ。
 * @param {object} [messageContext] - メッセージに関連する追加のコンテキスト情報。
 * @param {Error} [error] - エラーオブジェクト。
 */
export const alert = (
  message: string,
  messageContext?: object,
  error?: Error,
) => {
  if (!IS_MONITORING_ENABLED) return;
  if (!IS_DATADOG_ENABLED) {
    console.error(message, messageContext, error);
    return;
  }
  datadogLogs.logger.alert(message, messageContext, error);
};

/**
 * 緊急度: レベル3
 *
 * システムの一部が重大な問題で機能しなくなった状況を記録します。
 *
 * @param {string} message - クリティカルメッセージ。
 * @param {object} [messageContext] - メッセージに関連する追加のコンテキスト情報。
 * @param {Error} [error] - エラーオブジェクト。
 */
export const critical = (
  message: string,
  messageContext?: object,
  error?: Error,
) => {
  if (!IS_MONITORING_ENABLED) return;
  if (!IS_DATADOG_ENABLED) {
    console.error(message, messageContext, error);
    return;
  }
  datadogLogs.logger.critical(message, messageContext, error);
};

/**
 * 緊急度: レベル4
 *
 * アプリケーションで発生した重大なエラーを記録するために使用します。
 *
 * @param {string} message - エラーの説明メッセージ。
 * @param {object} [messageContext] - エラーに関連する追加のコンテキスト情報。
 * @param {Error} [error] - エラーオブジェクト。
 */
export const error = (
  message: string,
  messageContext?: object,
  error?: Error,
) => {
  if (!IS_MONITORING_ENABLED) return;
  if (!IS_DATADOG_ENABLED) {
    console.error(message, messageContext, error);
    return;
  }
  datadogLogs.logger.error(message, messageContext, error);
};

/**
 * 緊急度: レベル5
 *
 * システムの潜在的な問題を記録し、注意喚起を行うために使用します。
 *
 * @param {string} message - 警告メッセージ。
 * @param {object} [messageContext] - 警告に関連する追加のコンテキスト情報。
 * @param {Error} [error] - 任意のエラーオブジェクト（デバッグ目的）。
 */
export const warn = (
  message: string,
  messageContext?: object,
  error?: Error,
) => {
  if (!IS_MONITORING_ENABLED) return;
  if (!IS_DATADOG_ENABLED) {
    console.warn(message, messageContext, error);
    return;
  }
  datadogLogs.logger.warn(message, messageContext, error);
};

/**
 * 緊急度: レベル6
 *
 * 重要だが緊急性の低いイベントを記録するために使用します。
 *
 * @param {string} message - 通知メッセージ。
 * @param {object} [messageContext] - メッセージに関連する追加のコンテキスト情報。
 * @param {Error} [error] - 任意のエラーオブジェクト（デバッグ目的）。
 */
export const notice = (
  message: string,
  messageContext?: object,
  error?: Error,
) => {
  if (!IS_MONITORING_ENABLED) return;
  if (!IS_DATADOG_ENABLED) {
    console.info(message, messageContext, error);
    return;
  }
  datadogLogs.logger.notice(message, messageContext, error);
};

/**
 * 緊急度: レベル7
 *
 * システム内の通常の操作やイベントを記録するために使用します。
 *
 * @param {string} message - 情報メッセージ。
 * @param {object} [messageContext] - メッセージに関連する追加のコンテキスト情報。
 * @param {Error} [error] - 任意のエラーオブジェクト（デバッグ目的）。
 */
export const info = (
  message: string,
  messageContext?: object,
  error?: Error,
) => {
  if (!IS_MONITORING_ENABLED) return;
  if (!IS_DATADOG_ENABLED) {
    console.info(message, messageContext, error);
    return;
  }
  datadogLogs.logger.info(message, messageContext, error);
};

/**
 * 緊急度: レベル8
 *
 * トラブルシューティングや詳細な動作確認のために使用します。
 *
 * @param {string} message - デバッグメッセージ。
 * @param {object} [messageContext] - メッセージに関連する追加のコンテキスト情報。
 * @param {Error} [error] - 任意のエラーオブジェクト（デバッグ目的）。
 */
export const debug = (
  message: string,
  messageContext?: object,
  error?: Error,
) => {
  if (!IS_MONITORING_ENABLED) return;
  if (!IS_DATADOG_ENABLED) {
    console.debug(message, messageContext, error);
    return;
  }
  datadogLogs.logger.debug(message, messageContext, error);
};

/**
 * 緊急度: レベル9
 *
 * 正常に完了した操作や成功を記録するために使用します。
 *
 * @param {string} message - OKメッセージ。
 * @param {object} [messageContext] - メッセージに関連する追加のコンテキスト情報。
 * @param {Error} [error] - 任意のエラーオブジェクト（デバッグ目的）。
 */
export const ok = (message: string, messageContext?: object, error?: Error) => {
  if (!IS_MONITORING_ENABLED) return;
  if (!IS_DATADOG_ENABLED) {
    console.info(message, messageContext, error);
    return;
  }
  datadogLogs.logger.ok(message, messageContext, error);
};
